import {
  OvenCookHistoryItem,
  ReviewSummary,
  useMealReviewSummaries,
} from '@tovala/browser-apis-combinedapi'
import { useCallback, useEffect, useRef, useState } from 'react'

import { events, sourceIDs } from 'analytics/events'
import { useUser } from 'contexts/user'
import { useFirstOvenCookHistory } from 'hooks/cookHistory'
import { useOrderHistory } from 'hooks/orderHistory'
import { track } from 'utils/analytics'

import MealRatingDialog, { RatingDialogCloseResult } from './MealRatingDialog'
import MultiMealRatingDialog from './MultiMealRatingDialog'
import {
  getRatingPromptPreview,
  getRatingPromptPreviewToShow,
  RatingPromptPreview,
} from './previews'
import {
  getMealsToRate,
  getRatingPromptHistory,
  getRatingPromptToShow,
  hasViewedRatingOnboarding,
  isRatingPromptSuppressed,
  joinMealIDs,
  RatingPromptToShow,
  recordRatingPromptDismissed,
  recordRatingPromptRated,
  recordRatingPromptShown,
  resetRatingOnboardingViewed,
} from './utils'

export interface DueRatingPrompt {
  countsTowardPromptLimits: boolean
  promptToShow: RatingPromptToShow
}

function trackMealCookedReviewPrompt(promptToShow: RatingPromptToShow) {
  track(
    events.MEAL_COOKED_REVIEW_PROMPT,
    promptToShow.type === 'multi'
      ? {
          meal_id: joinMealIDs(
            promptToShow.mealsToRate.map(({ meal }) => meal.id),
          ),
          source_id: sourceIDs.MULTI_PROMPT,
        }
      : {
          meal_id: promptToShow.mealToRate.meal.id,
          source_id: sourceIDs.SINGLE_PROMPT,
        },
  )
}

export function useRatingPrompt({
  canOpenPrompt,
  onPromptDue,
}: {
  canOpenPrompt: boolean
  onPromptDue(ratingPrompt: DueRatingPrompt): void
}) {
  const { user } = useUser()

  const { data: cookHistory, refetch: refetchCookHistory } =
    useFirstOvenCookHistory({ userID: user.id })
  const {
    data: mealReviewSummaries,
    isError: hasMealReviewSummariesError,
    isPending: isPendingMealReviewSummaries,
    refetch: refetchMealReviewSummaries,
  } = useMealReviewSummaries({ userID: user.id })
  const { isLoading: isLoadingOrderHistory, receipts } = useOrderHistory({
    user,
  })

  const [ratingPromptPreview] = useState(() =>
    getRatingPromptPreview({ search: window.location.search }),
  )

  const latestPromptStateRef = useRef({ canOpenPrompt, onPromptDue, receipts })

  useEffect(() => {
    latestPromptStateRef.current = { canOpenPrompt, onPromptDue, receipts }
  })

  const canPromptNow = useCallback(
    (now: Date) =>
      latestPromptStateRef.current.canOpenPrompt &&
      !isRatingPromptSuppressed({
        history: getRatingPromptHistory(user.id),
        now,
      }),
    [user.id],
  )

  const openPromptIfMealIsDue = useCallback(
    ({
      cookHistory,
      mealReviewSummaries,
    }: {
      cookHistory: OvenCookHistoryItem[]
      mealReviewSummaries: ReviewSummary
    }) => {
      const now = new Date()
      if (!canPromptNow(now)) {
        return
      }

      const { onPromptDue, receipts } = latestPromptStateRef.current
      const history = getRatingPromptHistory(user.id)

      const promptToShow = getRatingPromptToShow({
        hasViewedOnboarding: hasViewedRatingOnboarding(user.id),
        mealsToRate: getMealsToRate({
          cookHistory,
          now,
          orderHistoryReceipts: receipts,
          reviewedMealIDs: mealReviewSummaries.reviews.map(
            (review) => review.mealId,
          ),
        }),
        seenSingleMealIDs: history.seenSingleMealIDs,
      })
      if (!promptToShow) {
        return
      }

      recordRatingPromptShown({
        now,
        singleMealID:
          promptToShow.type === 'single'
            ? promptToShow.mealToRate.meal.id
            : undefined,
        userID: user.id,
      })
      onPromptDue({ countsTowardPromptLimits: true, promptToShow })
      trackMealCookedReviewPrompt(promptToShow)
    },
    [canPromptNow, user.id],
  )

  const openPreviewPrompt = useCallback(
    ({
      mealReviewSummaries,
      ratingPromptPreview,
    }: {
      mealReviewSummaries: ReviewSummary | undefined
      ratingPromptPreview: RatingPromptPreview
    }) => {
      const { canOpenPrompt, onPromptDue, receipts } =
        latestPromptStateRef.current
      if (!canOpenPrompt) {
        return
      }

      const promptToShow = getRatingPromptPreviewToShow({
        now: new Date(),
        orderHistoryReceipts: receipts,
        preview: ratingPromptPreview,
        reviewedMealIDs:
          mealReviewSummaries?.reviews.map((review) => review.mealId) ?? [],
      })
      if (promptToShow) {
        if (ratingPromptPreview.type === 'onboarding') {
          resetRatingOnboardingViewed(user.id)
        }

        onPromptDue({ countsTowardPromptLimits: false, promptToShow })
        trackMealCookedReviewPrompt(promptToShow)
      }
    },
    [user.id],
  )

  const hasCheckedOnAppOpenRef = useRef(false)

  useEffect(() => {
    if (
      hasCheckedOnAppOpenRef.current ||
      !canOpenPrompt ||
      isLoadingOrderHistory ||
      isPendingMealReviewSummaries
    ) {
      return
    }

    if (ratingPromptPreview) {
      hasCheckedOnAppOpenRef.current = true
      openPreviewPrompt({ mealReviewSummaries, ratingPromptPreview })
      return
    }

    if (!cookHistory || !mealReviewSummaries || hasMealReviewSummariesError) {
      return
    }

    hasCheckedOnAppOpenRef.current = true
    openPromptIfMealIsDue({ cookHistory, mealReviewSummaries })
  }, [
    canOpenPrompt,
    cookHistory,
    hasMealReviewSummariesError,
    isLoadingOrderHistory,
    isPendingMealReviewSummaries,
    mealReviewSummaries,
    openPreviewPrompt,
    openPromptIfMealIsDue,
    ratingPromptPreview,
  ])

  const hasLoadedCookHistory = cookHistory !== undefined

  useEffect(() => {
    const handleVisibilityChange = async () => {
      // Returning to the tab is the web equivalent of reopening the app. Cook
      // history having loaded is what proves this user has an oven whose history
      // can be refetched.
      if (
        document.visibilityState !== 'visible' ||
        !hasCheckedOnAppOpenRef.current ||
        !hasLoadedCookHistory ||
        !canPromptNow(new Date())
      ) {
        return
      }

      // React Query refetches these on the same visibilitychange since they're
      // always stale, so reuse that fetch rather than cancelling and repeating it.
      const [cookHistoryResult, mealReviewSummariesResult] = await Promise.all([
        refetchCookHistory({ cancelRefetch: false }),
        refetchMealReviewSummaries({ cancelRefetch: false }),
      ])

      if (
        cookHistoryResult.data &&
        mealReviewSummariesResult.data &&
        !mealReviewSummariesResult.isError
      ) {
        openPromptIfMealIsDue({
          cookHistory: cookHistoryResult.data,
          mealReviewSummaries: mealReviewSummariesResult.data,
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [
    canPromptNow,
    hasLoadedCookHistory,
    openPromptIfMealIsDue,
    refetchCookHistory,
    refetchMealReviewSummaries,
  ])
}

const RatingPromptDialog = ({
  onClose,
  ratingPrompt: { countsTowardPromptLimits, promptToShow },
}: {
  onClose(): void
  ratingPrompt: DueRatingPrompt
}) => {
  const { user } = useUser()

  const handleClose = ({ wasRated }: RatingDialogCloseResult) => {
    if (countsTowardPromptLimits) {
      if (wasRated) {
        recordRatingPromptRated(user.id)
      } else {
        recordRatingPromptDismissed({ now: new Date(), userID: user.id })
      }
    }

    onClose()
  }

  return promptToShow.type === 'multi' ? (
    <MultiMealRatingDialog
      mealsToRate={promptToShow.mealsToRate}
      onClose={handleClose}
      sourceID={sourceIDs.MULTI_PROMPT}
    />
  ) : (
    <MealRatingDialog
      meal={promptToShow.mealToRate.meal}
      onClose={handleClose}
      sourceID={sourceIDs.SINGLE_PROMPT}
    />
  )
}

export default RatingPromptDialog
